import React from 'react';
import {
  Box,
  Flex,
  Progress,
  Skeleton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Text,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { FileText, CheckSquare, FolderOpen, ClockCounterClockwise } from 'phosphor-react';
import ContratosTab from './tabs/ContratosTab';
import ChecklistTab from './tabs/ChecklistTab';
import DocumentosTab from './tabs/DocumentosTab';
import HistoricoTab from './tabs/HistoricoTab';
import { IEventoDetalhe, IEventoResumo, useEventos } from '../../../hooks/useEventos';
import { IDataNoPagination } from '../../../types/shared';
import { formatCurrency } from '../../../utils/FormatFunctions';

function Cartao({
  titulo,
  valor,
  sub,
  subCor = 'gray.500',
  children,
}: {
  titulo: string;
  valor: React.ReactNode;
  sub?: string;
  subCor?: string;
  children?: React.ReactNode;
}) {
  return (
    <Box
      flex={{ base: '1 1 140px', md: '1 1 180px' }}
      bg="white"
      borderRadius="14px"
      border="1px solid"
      borderColor="gray.100"
      px={4}
      py={3}
    >
      <Text fontSize="12px" fontWeight="500" color="gray.500">
        {titulo}
      </Text>
      <Text
        fontSize="22px"
        fontWeight="700"
        color="gray.800"
        letterSpacing="-0.02em"
        whiteSpace="nowrap"
      >
        {valor}
      </Text>
      {children}
      {sub && (
        <Text fontSize="12px" color={subCor} mt={children ? 1.5 : 0}>
          {sub}
        </Text>
      )}
    </Box>
  );
}

/** Os números macro do evento. Some se a rota falhar (back sem a rota, cliente do portal). */
function ResumoDoEvento({ eventoId }: { eventoId: number }) {
  const { getResumoEvento } = useEventos();
  // Embaixo de 'evento-detalhe': quem já invalida o evento (contrato novo, card movido) atualiza o resumo junto.
  const { data, isLoading } = useQuery<IDataNoPagination<IEventoResumo>>({
    queryKey: ['evento-detalhe', eventoId, 'resumo'],
    queryFn: getResumoEvento,
    retry: false,
  });
  if (isLoading) {
    return (
      <Flex gap={3} mb={4} flexWrap="wrap">
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton key={i} flex="1 1 180px" h="84px" borderRadius="14px" />
        ))}
      </Flex>
    );
  }
  // refetch que falha com dado já na tela mantém os cartões; some só sem dado (rota ausente, 403)
  const r = data?.data;
  if (!r) return null;

  const pct = (n: number, de = r.total) => (de ? Math.round((n / de) * 100) : 0);
  // cancelado nunca vai ser assinado: fica fora do "a / total"
  const cancelados = r.por_status?.find(s => s.status === 'cancelado')?.total ?? 0;
  const assinaveis = r.total - cancelados;
  return (
    <Flex gap={3} mb={4} flexWrap="wrap">
      <Cartao
        titulo="Contratos"
        valor={r.total}
        sub={cancelados ? `${cancelados} cancelado${cancelados > 1 ? 's' : ''}` : undefined}
      />
      <Cartao titulo="Feitos pelo robô" valor={`${pct(r.robo)}%`} sub={`${r.robo} de ${r.total}`} />
      <Cartao titulo="Assinados" valor={`${r.assinados} / ${assinaveis}`}>
        <Progress
          value={pct(r.assinados, assinaveis)}
          size="xs"
          borderRadius="full"
          colorScheme="green"
          bg="gray.100"
          mt={1}
        />
      </Cartao>
      <Cartao
        titulo="Aguardando assinatura"
        valor={r.aguardando}
        sub={r.parados ? `${r.parados} parados há mais de 7 dias` : 'nenhum parado'}
        subCor={r.parados ? 'orange.600' : 'gray.500'}
      />
      <Cartao
        titulo="Valor contratado"
        valor={formatCurrency(r.valor_total)}
        sub="todos os contratos, sem os cancelados"
      />
    </Flex>
  );
}

/**
 * Aba Gerencial da pagina do evento: os numeros macro do evento e as sub-abas contratos, checklist, documentos e
 * historico. Cada sub-aba so' busca quando aberta (e fica montada depois, sem perder edicao): a de Documentos faz
 * um HEAD no S3 por anexo.
 */
export default function EventoGerencial({ evento }: { evento: IEventoDetalhe }) {
  const eventoId = evento.id;

  return (
    <>
      <ResumoDoEvento eventoId={eventoId} />
      <Tabs variant="unstyled" size="sm" isLazy lazyBehavior="keepMounted">
        <TabList
          bg="white"
          borderRadius="12px"
          border="1px solid"
          borderColor="gray.100"
          p={1}
          gap={1}
          mb={4}
        >
          {[
            { icon: FileText, label: 'Contratos' },
            { icon: CheckSquare, label: 'Checklist' },
            { icon: FolderOpen, label: 'Documentos' },
            { icon: ClockCounterClockwise, label: 'Histórico' },
          ].map(tab => (
            <Tab
              key={tab.label}
              flex={1}
              fontSize="12px"
              fontWeight="500"
              py={2}
              borderRadius="10px"
              color="gray.400"
              _selected={{ bg: 'gray.900', color: 'white' }}
              transition="all 0.15s ease"
            >
              <tab.icon size={14} style={{ marginRight: 5 }} />
              {tab.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          <TabPanel px={0} py={0}>
            <ContratosTab eventoId={eventoId} />
          </TabPanel>
          <TabPanel px={0} py={0}>
            <ChecklistTab eventoId={eventoId} />
          </TabPanel>
          <TabPanel px={0} py={0}>
            <DocumentosTab eventoId={eventoId} />
          </TabPanel>
          <TabPanel px={0} py={0}>
            <HistoricoTab eventoId={eventoId} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
}
